import Decimal from 'decimal.js-light';
import { useState } from 'react';

import { tokenFormatter } from '@/lib/formatting';

import { LONG_TOKEN_SYMBOL_LENGTH } from '@/components/TokenProfile/TokenProfileHeader';

import { fontStyles } from '@/styles/typography';

import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { truncate } from '@/utils/string';

import type { TradeDirection } from '../types';

interface ExchangeRateProps {
  /** Buy token symbol. */
  buyTokenSymbol: string;
  /** Sell token symbol. */
  sellTokenSymbol: string;
  /** Whether the exchange rate is loading. */
  isLoading?: boolean;
  /** Exchange rate for the token pair. */
  exchangeRate: { sell: Decimal; buy: Decimal } | undefined;
}

/**
 * Renders the exchange rate for the token pair with the ability to swap the
 * rate back and forth between them.
 */
export const ExchangeRate = ({
  buyTokenSymbol,
  sellTokenSymbol,
  isLoading,
  exchangeRate,
}: ExchangeRateProps): React.ReactElement | null => {
  // state
  const [exchangeRateSide, setExchangeRateSide] = useState<TradeDirection>('buy');

  if (!exchangeRate) {
    return <span className="inline-flex items-center text-token-gray-700">-</span>;
  }

  const isLongTokenSymbol =
    buyTokenSymbol.length > LONG_TOKEN_SYMBOL_LENGTH ||
    sellTokenSymbol.length > LONG_TOKEN_SYMBOL_LENGTH;

  // vars
  let to: string, from: string;
  if (exchangeRateSide === 'buy') {
    to = buyTokenSymbol;
    from = sellTokenSymbol;
  } else {
    to = sellTokenSymbol;
    from = buyTokenSymbol;
  }
  const rate = `1 ${to} = ${tokenFormatter.format(exchangeRate[exchangeRateSide], { symbol: from })}`;
  const truncatedRate = `1 ${truncate(to, LONG_TOKEN_SYMBOL_LENGTH, true)} = ${tokenFormatter.format(exchangeRate[exchangeRateSide], { symbol: truncate(from, LONG_TOKEN_SYMBOL_LENGTH, true) })}`;

  return (
    <span className="inline-flex items-center">
      <button
        aria-label="switch exchange rate"
        className={fontStyles({
          variant: 'BODY_SM',
          class:
            'leading-[1] text-token-gray-700 bg-transparent border-0 p-0 hover:text-token-gray-800 disabled:text-token-gray-500 disabled:cursor-default disabled:hover:text-token-gray-500 disabled:hover:cursor-default`',
        })}
        disabled={isLoading}
        onClick={() => setExchangeRateSide(exchangeRateSide === 'buy' ? 'sell' : 'buy')}
      >
        <MatchaTooltip>
          <MatchaTooltipTrigger>
            <span>{truncatedRate}</span>
          </MatchaTooltipTrigger>
          <MatchaTooltipContent>{rate}</MatchaTooltipContent>
          {isLongTokenSymbol && (
            <MatchaTooltipPopover sideOffset={0} style={{ maxWidth: 'fit-content' }} />
          )}
        </MatchaTooltip>
      </button>
    </span>
  );
};
